/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Jun 21, 2026 by hoarfroster
 *
 */

import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { lazy } from 'react';
import HomePage from '@pages/home';
import AppLayout from '@pages/app/layout';
import { Protected } from '@utils/use-protection';
import { UserRole } from '@models/user/user.ts';
import NotFoundPage from '@pages/not-found';

const SignInPage = lazy(() => import('@pages/auth/sign-in')),
    RegisterPage = lazy(() => import('@pages/auth/register')),
    UserInfoPage = lazy(() => import('@pages/user/user-info')),
    CredentialsPage = lazy(() => import('@pages/user/credentials')),
    OAuthCallbackPage = lazy(() => import('@pages/auth/oauth-callback')),
    SettingsLayout = lazy(() => import('@pages/settings/layout')),
    ProfileSettingsPage = lazy(() => import('@pages/settings/profile')),
    SecuritySettingsPage = lazy(() => import('@pages/settings/security')),
    PreferencesSettingsPage = lazy(() => import('@pages/settings/preferences')),
    NotePage = lazy(() => import('@pages/note/note')),
    NoteSearchPage = lazy(() => import('@pages/note/search')),
    NoteCollectionsPage = lazy(() => import('@pages/note/collections')),
    NotePinnedPage = lazy(() => import('@pages/note/pinned')),
    NoteEditPage = lazy(() => import('@pages/note/note-edit')),
    RecipeListPage = lazy(() => import('@pages/cooking/recipes/recipe-list')),
    RecipeDetailPage = lazy(() => import('@pages/cooking/recipes/recipe-detail')),
    RecipeEditPage = lazy(() => import('@pages/cooking/recipes/recipe-edit-page')),
    CuisineTypeDetailPage = lazy(() => import('@pages/cooking/cuisine-type-detail')),
    IngredientsPage = lazy(() => import('@pages/cooking/ingredients')),
    ToolsPage = lazy(() => import('@pages/cooking/tools')),
    CookingSearchPage = lazy(() => import('@pages/cooking/search')),
    FilePage = lazy(() => import('@pages/file')),
    FileListPage = lazy(() => import('@pages/file/browse')),
    LivePhotoUploadPage = lazy(() => import('@pages/file/live-photo-upload')),
    FileTagListPage = lazy(() => import('@pages/file/tag')),
    MomentsListPage = lazy(() => import('@pages/moment/moments')),
    MomentPage = lazy(() => import('@pages/moment/moment')),
    RemarkListPage = lazy(() => import('@pages/remark/RemarkListPage')),
    ContactPage = lazy(() => import('@pages/contact')),
    PublicContactInvitationPage = lazy(() => import('@pages/contact/invite')),
    ActivityWorkspaceLayout = lazy(() => import('@pages/time')),
    ActivityBoardPage = lazy(() => import('@pages/time/board')),
    ActivityTagListPage = lazy(() => import('@pages/time/tags')),
    CalendarPage = lazy(() => import('@pages/time/calendar')),
    AdminPreferencesPage = lazy(() => import('@pages/admin/preferences')),
    AdminUsersPage = lazy(() => import('@pages/admin/users')),
    AdminWidgetsPage = lazy(() => import('@pages/admin/widgets')),
    AdminContactInvitationsPage = lazy(() => import('@pages/admin/contact-invitations')),
    AdminCookingLayout = lazy(() => import('@pages/admin/cooking/layout')),
    AdminCookingDashboardPage = lazy(() => import('@pages/admin/cooking')),
    AdminCookingRecipesPage = lazy(() => import('@pages/admin/cooking/recipes')),
    AdminCookingRecipeEditPage = lazy(() => import('@pages/admin/cooking/recipes/edit')),
    AdminCookingIngredientsPage = lazy(() => import('@pages/admin/cooking/ingredients')),
    AdminCookingToolsPage = lazy(() => import('@pages/admin/cooking/tools')),
    AdminCuisineTypesPage = lazy(() => import('@pages/admin/cooking/cuisine-types')),
    MailPage = lazy(() => import('@pages/mail')),
    PostRecordListPage = lazy(() => import('@pages/post-record/list')),
    PostRecordDetailPage = lazy(() => import('@pages/post-record/detail'));

/* Lazy imports start here */
const TreeSearchPage = lazy(() => import('@pages/interactive-demos/tree-search'));
const SortingPage = lazy(() => import('@pages/interactive-demos/sorting'));
/* Lazy imports end here */

function App() {
    return (
        <BrowserRouter>
            <AppRoutes />
        </BrowserRouter>
    );
}

function AppRoutes() {
    return (
        <main className="h-max min-h-full w-full flex-1">
            <Routes>
                <Route element={<AppLayout />}>
                    <Route index element={<HomePage />} />
                    <Route path="/settings" element={<SettingsLayout />}>
                        <Route path="profile" element={<ProfileSettingsPage />} />
                        <Route path="security" element={<SecuritySettingsPage />} />
                        <Route path="preferences" element={<PreferencesSettingsPage />} />
                    </Route>
                    <Route path="/moment">
                        <Route index element={<MomentsListPage />} />
                        <Route path=":id" element={<MomentPage />} />
                    </Route>
                    <Route path="/note">
                        <Route index element={<NotePage />} />
                        <Route path="search" element={<NoteSearchPage />} />
                        <Route
                            path="collections"
                            element={
                                <Protected minRole={UserRole.SuperAdmin}>
                                    <NoteCollectionsPage />
                                </Protected>
                            }
                        />
                        <Route
                            path="pinned"
                            element={
                                <Protected minRole={UserRole.SuperAdmin}>
                                    <NotePinnedPage />
                                </Protected>
                            }
                        />
                        <Route path=":path">
                            <Route index element={<NotePage />} />
                            <Route path="edit" element={<NoteEditPage />} />
                        </Route>
                    </Route>
                    <Route path="/remark" element={<RemarkListPage />} />
                    <Route path="/cooking">
                        <Route path="search" element={<CookingSearchPage />} />
                        <Route path="recipes">
                            <Route index element={<RecipeListPage />} />
                            <Route path=":id">
                                <Route index element={<RecipeDetailPage />} />
                                <Route path="edit" element={<RecipeEditPage />} />
                            </Route>
                            <Route path="new" index element={<RecipeEditPage />} />
                        </Route>
                        <Route path="cuisine-types">
                            <Route path=":id" element={<CuisineTypeDetailPage />} />
                        </Route>
                        <Route path="ingredients">
                            <Route index element={<IngredientsPage />} />
                            <Route path=":id">
                                <Route index element={<IngredientsPage />} />
                            </Route>
                        </Route>
                        <Route path="tools">
                            <Route index element={<ToolsPage />} />
                            <Route path=":id">
                                <Route index element={<ToolsPage />} />
                            </Route>
                        </Route>
                    </Route>
                    <Route path="/file">
                        <Route index element={<FilePage />} />
                        <Route path="browse">
                            <Route index element={<FileListPage />} />
                            <Route path=":path" element={<FileListPage />} />
                        </Route>
                        <Route path="tag" element={<FileTagListPage />} />
                        <Route path="live-photo-upload" element={<LivePhotoUploadPage />} />
                    </Route>
                    <Route path="/user">
                        <Route path="" element={<UserInfoPage />} />
                        <Route path=":id" element={<UserInfoPage />} />
                        <Route path="credentials" element={<CredentialsPage />} />
                    </Route>
                    <Route path="/auth">
                        <Route path="sign-in" element={<SignInPage />} />
                        <Route path="oauth2" element={<OAuthCallbackPage />} />
                        <Route path="register" element={<RegisterPage />} />
                    </Route>
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/contact/invite/:token" element={<PublicContactInvitationPage />} />
                    <Route path="/time" element={<ActivityWorkspaceLayout />}>
                        <Route index element={<Navigate to="project" replace />} />
                        <Route path="board" element={<ActivityBoardPage />} />
                        <Route path="project" element={<ActivityBoardPage />} />
                        <Route path="project/:projectId/calendar" element={<CalendarPage />} />
                        <Route path="project/:projectId" element={<ActivityBoardPage />} />
                        <Route path="inbox" element={<ActivityBoardPage />} />
                        <Route path="inbox/calendar" element={<CalendarPage />} />
                        <Route path="today" element={<ActivityBoardPage />} />
                        <Route path="today/calendar" element={<CalendarPage />} />
                        <Route path="upcoming" element={<ActivityBoardPage />} />
                        <Route path="upcoming/calendar" element={<CalendarPage />} />
                        <Route path="calendar" element={<CalendarPage />} />
                        <Route path="filtersAndLabels" element={<ActivityTagListPage />} />
                    </Route>
                    <Route path="/mail" element={<MailPage />} />
                    <Route path="/post-record">
                        <Route index element={<PostRecordListPage />} />
                        <Route path=":id" element={<PostRecordDetailPage />} />
                    </Route>
                    <Route path="/mail/post-record" element={<Navigate to="/post-record" replace />} />
                    <Route path="/admin">
                        <Route
                            path="preferences"
                            element={
                                <Protected minRole={UserRole.SuperAdmin}>
                                    <AdminPreferencesPage />
                                </Protected>
                            }
                        />
                        <Route
                            path="users"
                            element={
                                <Protected minRole={UserRole.Admin}>
                                    <AdminUsersPage />
                                </Protected>
                            }
                        />
                        <Route
                            path="contact-invitations"
                            element={
                                <Protected minRole={UserRole.SuperAdmin}>
                                    <AdminContactInvitationsPage />
                                </Protected>
                            }
                        />
                        <Route
                            path="widgets"
                            element={
                                <Protected minRole={UserRole.SuperAdmin}>
                                    <AdminWidgetsPage />
                                </Protected>
                            }
                        />
                        <Route path="cooking" element={<AdminCookingLayout />}>
                            <Route index element={<AdminCookingDashboardPage />} />
                            <Route path="recipes">
                                <Route index element={<AdminCookingRecipesPage />} />
                                <Route path="new" element={<AdminCookingRecipeEditPage />} />
                                <Route path=":id" element={<AdminCookingRecipeEditPage />} />
                            </Route>
                            <Route path="ingredients" element={<AdminCookingIngredientsPage />} />
                            <Route path="tools" element={<AdminCookingToolsPage />} />
                            <Route path="cuisine-types" element={<AdminCuisineTypesPage />} />
                        </Route>
                    </Route>
                    <Route path="/interactive-demos">
                        <Route path="tree-search" element={<TreeSearchPage />} />
                        <Route path="sorting" element={<SortingPage />} />
                    </Route>
                    <Route path="*" element={<NotFoundPage />} />
                </Route>
            </Routes>
        </main>
    );
}

export default App;
